import axios from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_FOOD_SERVICE_ENDPOINT || 'http://localhost:4000';

export interface FoodBrand {
  id: number;
  name_en: string;
  name_ar?: string | null;
}

export interface FoodCategory {
  id: number;
  name_en: string;
  name_ar?: string | null;
}

export interface FoodNutrition {
  calories?: number | string | null;
  protein_g?: number | string | null;
  fat_g?: number | string | null;
  carbohydrates_g?: number | string | null;
  fiber_g?: number | string | null;
  sugar_g?: number | string | null;
  sodium_mg?: number | string | null;

  additional?: string | Record<string, unknown> | null;
}

export interface FoodMeasure {
  id: number;
  measure_name: string;
  quantity: number | string | null;
  unit: string | null;
  gram_weight: number | string | null;
  display_order?: number;
  is_default?: boolean;
}

export interface FoodSynonym {
  id: number;
  language: string;
  synonym: string;
  normalized_synonym?: string | null;
}

export interface Food {
  id: number;
  name_en: string;
  name_ar?: string | null;
  description?: string | null;

  brand?: FoodBrand | null;
  category?: FoodCategory | null;

  nutrition: FoodNutrition;

  //   nutrition_json?: Record<string, unknown> | null;

  barcode?: string | null;
  image_url?: string | null;

  usda_fdc_id?: number | null;

  is_verified?: boolean;
  is_auto_imported?: boolean;
  is_active?: boolean;

  usage_count?: number;
  last_used_at?: string | null;

  created_at?: string;
  updated_at?: string;

  measures?: FoodMeasure[];
  synonyms?: FoodSynonym[];
}

export interface FoodPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface FoodListResponse {
  foods: Food[];
  pagination: FoodPagination;
}

interface ApiResponse<T> {
  success?: boolean;
  message?: string;
  data?: T;
}

export interface FoodListFilters {
  food_name?: string;
  brand_name?: string;
}

export interface CreateFoodPayload {
  name_en: string;
  name_ar?: string | null;

  description?: string | null;

  brand_name?: string | null;

  calories?: number | null;
  protein_g?: number | null;
  fat_g?: number | null;
  carbohydrates_g?: number | null;
  fiber_g?: number | null;
  sugar_g?: number | null;
  sodium_mg?: number | null;

  nutrition_json?: Record<string, number | null> | null;

  barcode?: string | null;
  image_url?: string | null;
}

export async function getFoods(
  page = 1,
  limit = 20,
  filters: FoodListFilters = {}
): Promise<FoodListResponse> {
  try {
    const response = await axios.get<ApiResponse<FoodListResponse>>(
      `${API_BASE_URL}/v1/foods`,
      {
        params: {
          page,
          limit,
          food_name: filters.food_name || undefined,
          brand_name: filters.brand_name || undefined
        }
      }
    );

    return (
      response.data.data || {
        foods: [],
        pagination: {
          page,
          limit,
          total: 0,
          totalPages: 0
        }
      }
    );
  } catch (error: any) {
    console.error('Failed to fetch foods:', error);

    const message = error?.response?.data?.message || 'Failed to fetch foods.';

    throw new Error(message);
  }
}

export async function getFoodById(foodId: number): Promise<Food> {
  try {
    const response = await axios.get<ApiResponse<Food>>(
      `${API_BASE_URL}/v1/foods/${foodId}`
    );

    if (!response.data.data) {
      throw new Error('Food details were not found.');
    }

    return response.data.data;
  } catch (error: any) {
    console.error('Failed to fetch food details:', error);

    const message =
      error?.response?.data?.message || 'Failed to fetch food details.';

    throw new Error(message);
  }
}

export interface UpdateFoodPayload {
  name_en: string;
  name_ar?: string | null;

  description?: string | null;

  brand_name_en?: string | null;
  brand_name_ar?: string | null;

  serving_size?: number | null;
  serving_unit?: 'g' | 'ml' | 'item' | null;

  calories?: number | null;
  protein_g?: number | null;
  fat_g?: number | null;
  carbohydrates_g?: number | null;
  fiber_g?: number | null;
  sugar_g?: number | null;
  sodium_mg?: number | null;

  nutrition_json?: Record<string, number | null> | null;

  barcode?: string | null;
  image_url?: string | null;
}

export async function updateFood(
  foodId: number,
  payload: UpdateFoodPayload
): Promise<Food> {
  try {
    const response = await axios.put<ApiResponse<Food>>(
      `${API_BASE_URL}/v1/foods/${foodId}`,
      payload
    );

    if (!response.data.data) {
      throw new Error('Food was not updated.');
    }

    return response.data.data;
  } catch (error: any) {
    console.error('Failed to update food:', error);

    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      'Failed to update food.';

    throw new Error(message);
  }
}

export async function createFood(payload: CreateFoodPayload): Promise<{
  foodId: number;
  brandId: number | null;
}> {
  try {
    const response = await axios.post<
      ApiResponse<{
        foodId: number;
        brandId: number | null;
      }>
    >(`${API_BASE_URL}/v1/foods`, payload);

    if (!response.data.data) {
      throw new Error('Food was not created.');
    }

    return response.data.data;
  } catch (error: any) {
    console.error('Failed to create food:', error);

    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      'Failed to create food.';

    throw new Error(message);
  }
}

export async function deleteFood(foodId: number): Promise<void> {
  try {
    await axios.delete(`${API_BASE_URL}/v1/foods/${foodId}`);
  } catch (error: any) {
    console.error('Failed to delete food:', error);

    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      'Failed to delete food.';

    throw new Error(message);
  }
}
